import { gql, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import { Modal, Table } from '../../../../components';
import { Block, Button, Text, TextInput } from '../../../../components/layout';
import ButtonAction from '../../../../components/Table/components/ButtonAction';
import { SvgIconEdit } from '../../../../svgs';
import { store } from '../../../../utils/store';
import validateFormSchema from '../../../../utils/validateFormSchema';

const QUERY_SETTING = gql`
    query setting($key: String!) {
        setting(key: $key) {
            key
            value
        }
    }
`;

const MUTATION_SETTING_UPDATE = gql`
    mutation settingUpdate($key: String!, $value: Json!) {
        settingUpdate(key: $key, value: $value) {
            key
            value
        }
    }
`;

/**
 * Where a public form sends its submissions. One row, one email address.
 * The key identifies the form: "formations", "careers", "whistleblowing".
 */
function EmailSetting({ settingKey, title, description }) {
    const { language } = useContext(store);
    const [updateData, setUpdateData] = useState(null);
    const [settings, setSettings] = useState({});

    const [querySetting, { loading }] = useLazyQuery(QUERY_SETTING, {
        variables: { key: settingKey },
        onError: () => { },
        onCompleted: (data) => {
            if (data && data.setting) {
                setSettings(data.setting.value);
            }
        }
    });

    const [mutationSettingUpdate, { loading: loadingSettingUpdate }] = useMutation(MUTATION_SETTING_UPDATE, {
        onError: () => { },
        onCompleted: (data) => {
            if (data && data.settingUpdate) {
                setUpdateData(null);
                querySetting();
            }
        }
    });

    // querySetting is recreated each render, listing it as a dependency would loop
    useEffect(() => {
        querySetting();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        querySetting();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language]);

    return (
        <>
            <Block color="white" pb={28}>
                <Block row center pl={20} pt={10} pb={10}>
                    <Text size={18} height={32}>{title}</Text>
                </Block>
                <Block width="100%" height={1} color="greyAlmostWhite" />
                <Block>
                    <Table
                        type={2}
                        loading={loading}
                        columns={[
                            { label: "Email", key: "email" }
                        ]}
                        rows={
                            [
                                {
                                    email: settings.email,
                                    actions: <ButtonAction icon={<SvgIconEdit />} onClick={() => setUpdateData(settings)} />
                                }
                            ]
                        }
                    />
                </Block>
            </Block>
            <Modal
                title="Update"
                isOpen={!!updateData}
                onRequestClose={() => setUpdateData(null)}
            >
                <Text size={14} height={20}>{description || title}</Text>
                <Block mt={28}>
                    <Formik
                        enableReinitialize
                        initialValues={settings}
                        validateOnChange={false}
                        validateOnBlur={false}
                        validate={values => {
                            return validateFormSchema(
                                yup.object().shape({
                                    email: yup.string().email().required()
                                }),
                                values
                            )
                        }}
                        onSubmit={(values, { setSubmitting }) => {
                            setSubmitting(false);
                            mutationSettingUpdate({ variables: { key: settingKey, value: values } });
                        }}
                    >
                        {({
                            values,
                            errors,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                        }) => {
                            return (
                                <form onSubmit={handleSubmit} autoComplete="off">
                                    <Block>
                                        <Block>
                                            <Block>
                                                <TextInput name="email" label="Email" type="text" onChange={handleChange} onBlur={handleBlur} value={values.email} error={errors.email} />
                                            </Block>
                                        </Block>
                                        <Block mt={44}>
                                            <Button type="submit" text="Submit" loading={loadingSettingUpdate} />
                                        </Block>
                                    </Block>
                                </form>
                            )
                        }}
                    </Formik>
                </Block>
            </Modal>
        </>
    );
}

export default EmailSetting;
